import React, { useEffect, useState } from "react";
import Header from "./Header";
import run from "../utils/gemini";
import MovieCard from "./MovieCard";
import { FaSearch } from "react-icons/fa";
import CardShimmer from "./CardShimmer";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const SEARCH_CACHE_KEY = "netflix_gpt_search_cache_v1";

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [ids, setIds] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [searchingIds, setSearchingIds] = useState(false);

  const { data: user, isAuthChecked } = useSelector((store) => store.user);
  const apiKey = process.env.REACT_APP_OMDB_API_KEY;
  const navigate = useNavigate();

  const clearPersistedSearch = () => {
    try {
      localStorage.removeItem(SEARCH_CACHE_KEY);
    } catch (error) {
      console.error("Error clearing cached search data:", error);
    }
  };

  const saveSearchToCache = (nextState) => {
    try {
      localStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify(nextState));
    } catch (error) {
      console.error("Error saving cached search data:", error);
    }
  };

  const clearSearchState = () => {
    setSearchQuery("");
    setSearchTitle("");
    setIds([]);
    setSearchError("");
    clearPersistedSearch();
  };

  useEffect(() => {
    if (isAuthChecked && !user) {
      navigate("/");
    }
  }, [isAuthChecked, navigate, user]);

  useEffect(() => {
    try {
      const cachedSearch = localStorage.getItem(SEARCH_CACHE_KEY);
      if (!cachedSearch) return;

      const parsedSearch = JSON.parse(cachedSearch);
      if (!parsedSearch || typeof parsedSearch !== "object") return;

      setSearchQuery(
        typeof parsedSearch.searchQuery === "string"
          ? parsedSearch.searchQuery
          : ""
      );
      setSearchTitle(
        typeof parsedSearch.searchTitle === "string"
          ? parsedSearch.searchTitle
          : ""
      );
      setIds(Array.isArray(parsedSearch.ids) ? parsedSearch.ids : []);
    } catch (error) {
      console.error("Error reading cached search data:", error);
      clearPersistedSearch();
    }
  }, []);

  const fetchMovieData = async (movies) => {
    if (!apiKey) {
      setSearchError("OMDB API key is missing. Add it in .env.");
      return [];
    }

    try {
      setSearchingIds(true);
      const movieData = await Promise.all(
        movies.slice(1).map((movie) =>
          fetch(
            `https://www.omdbapi.com/?t=${encodeURIComponent(movie)}&apikey=${apiKey}`
          )
            .then((res) => res.json())
            .catch((error) => {
              console.error("Error fetching movie data:", error);
              return null;
            })
        )
      );

      const validMovieData = movieData.filter((data) => data && data.imdbID);
      setIds(validMovieData);
      return validMovieData;
    } catch (error) {
      console.error("Error:", error);
      return [];
    } finally {
      setSearchingIds(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      clearSearchState();
      return;
    }

    setSearchQuery(trimmedQuery);
    setIds([]);
    setSearchTitle("");
    setSearchError("");
    clearPersistedSearch();
    setIsAnalyzing(true);

    try {
      const result = await run(trimmedQuery);
      let cleanResult = result;

      if (typeof result === "string") {
        cleanResult = result.replace(/```json|```/g, "").trim();
      }

      const parsedResult =
        typeof cleanResult === "string"
          ? JSON.parse(cleanResult)
          : cleanResult;

      if (!Array.isArray(parsedResult) || parsedResult.length === 0) {
        throw new Error("Invalid recommendation response");
      }

      const nextSearchTitle =
        typeof parsedResult[0] === "string" ? parsedResult[0] : "";
      setSearchTitle(nextSearchTitle);

      let validMovieData = [];
      if (parsedResult.length > 1) {
        validMovieData = await fetchMovieData(parsedResult);
      }

      saveSearchToCache({
        searchQuery: trimmedQuery,
        searchTitle: nextSearchTitle,
        ids: validMovieData,
      });
    } catch (error) {
      console.error("Error during search:", error);
      setSearchError(
        "Unable to fetch recommendations right now. Please try again."
      );
      clearPersistedSearch();
    } finally {
      setIsAnalyzing(false);
    }
  };

  const isLoading = isAnalyzing || searchingIds;
  const shouldShowClearButton =
    !!searchQuery || !!searchTitle || ids.length > 0 || !!searchError;

  if (!isAuthChecked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <div className="h-14 w-14 border-4 border-neutral-700 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-categoriesbg text-white flex flex-col items-center pt-6 md:pt-24">
        <div className="w-full max-w-4xl mt-10 p-4 max-md:px-6">
          <form onSubmit={handleSearch} className="flex">
            <input
              type="text"
              placeholder="Type a genre or mood to explore..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              maxLength={500}
              className="flex-grow py-2 px-4 rounded-l-lg outline-none text-gray-900"
            />
            <button
              type="submit"
              className="bg-red-600 px-4 py-2 rounded-r-lg outline-none text-white font-semibold hover:bg-red-700 transition-colors"
            >
              <FaSearch className={`${isLoading ? "animate-pulse" : ""} text-lg`} />
            </button>
          </form>

          {shouldShowClearButton && (
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={clearSearchState}
                className="text-sm text-gray-300 hover:text-white hover:underline transition-colors"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        {isAnalyzing && !searchingIds && (
          <p className="text-gray-400 mt-3 md:mt-6 text-lg font-medium">
            Analyzing the user input...
          </p>
        )}

        {searchTitle && (
          <p className="text-lg font-medium md:font-semibold mb-4 mt-6 md:mt-10 px-4 max-w-4xl text-center">
            {searchTitle}
          </p>
        )}

        {isLoading && (
          <div className="mt-6">
            <CardShimmer count={12} val={6} />
          </div>
        )}

        {!isLoading && searchError && (
          <p className="text-red-400 mt-2 text-center px-4">{searchError}</p>
        )}

        <div className="w-full max-w-5xl p-8 md:p-4">
          {!isLoading && ids?.length > 0 ? (
            <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {ids?.map((movie) => (
                <MovieCard key={movie.imdbID} query={movie.imdbID} parameter="i" />
              ))}
            </div>
          ) : (
            !isLoading &&
            !searchTitle &&
            !searchError && (
              <div className="text-center mt-20">
                <h2 className="text-2xl font-bold text-gray-400">
                  Nothing here yet...
                </h2>
                <p className="text-gray-500 mt-2">
                  Enter a title or description above to get movie
                  recommendations.
                </p>
              </div>
            )
          )}

          {!isLoading && searchTitle && ids.length === 0 && !searchError && (
            <div className="text-center mt-10">
              <p className="text-gray-400">
                No movies found for this search. Try different keywords.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SearchPage;
